import { useMemo } from "react";
import { projectChartPoints, resolveChartConfig, signalOfSource } from "../../chartWidgets";
import { describeSignalDiagnostic } from "./diagnosticMeaning";
import type { ProductionHistoryWindow } from "@/features/devices/types";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import type { WidgetHistoryState } from "../../useProductionHistories";
import { resolveStopAlarm, useStopAlarm } from "../../useStopAlarm";
import { formatWidgetNumber, formatWidgetRelative } from "./format";
import { HeroCard, HeroMeaning, HeroWindowPicker, type HeroTone } from "./HeroCard";
import { TimeSeriesChart, type TimeSeriesPoint } from "./TimeSeriesChart";
import { TrendStats, useRateWindowStats } from "./TrendStats";
import { windowSpan } from "./windowPhrase";

interface RateHeroWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  history?: WidgetHistoryState;
  editable?: boolean;
  onConfigure?: () => void;
  /** Desfaz o "Gerar grafico": este widget e uma das faces do line-chart. */
  onHideChart?: () => void;
  /** Troca a janela direto no card (persistida pelo pai). */
  onChangeWindow?: (window: ProductionHistoryWindow) => void;
}

/**
 * Leitura ao vivo de uma taxa com sua série histórica. `rate` também cobre
 * potência e vazão: sem regra explícita, o valor não prova operação/parada.
 * Widgets legados sem kind preservam a interpretação anterior.
 */
export function RateHeroWidget({
  widget,
  value,
  history,
  editable,
  onConfigure,
  onHideChart,
  onChangeWindow,
}: RateHeroWidgetProps) {
  const config = resolveChartConfig(widget);
  const status = history?.status ?? "loading";
  // `reloading` = troca de janela: a linha ANTERIOR permanece visível
  // (atenuada) até a nova série chegar.
  const busy = history?.status === "reloading";
  const data =
    history?.status === "ready" || history?.status === "reloading" ? history.data : null;
  const decimals = widget.settings.decimals ?? 1;
  const unit = widget.settings.unit?.trim() || null;
  const isFresh = value.isAvailable;
  const rate = isFresh ? value.numericValue : null;

  // Durante o reloading, a linha exibida ainda é da janela anterior — o rótulo
  // e o eixo seguem a janela do DADO, não a recém-pedida.
  const dataWindow = (data?.window ?? config.window) as ProductionHistoryWindow;
  const phrase = windowSpan(busy ? dataWindow : config.window);

  // A linha (face) usa o ritmo médio por bucket — resolução cheia, sem bins.
  // Buckets sem valor são PRESERVADOS como `value: null`: é o que faz a linha
  // interromper num timeout Modbus ou numa falha AIN em vez de ligar os dois
  // lados do buraco.
  const points: TimeSeriesPoint[] = useMemo(() => data
    ? projectChartPoints(data.points, "rate", dataWindow)
    : [], [data, dataWindow]);
  const hasPlottable = points.some((point) => point.value !== null);
  // Media/extremos da JANELA, dos mesmos buckets da curva. O destaque e o agora.
  const stats = useRateWindowStats(data);

  // Compatibilidade apenas para widgets legados. Um kind `rate` conhecido
  // não autoriza inferir funcionamento: potência negativa também é uma taxa.
  const isLegacyVelocity =
    signalOfSource(widget.source) === "v" &&
    widget.settings.signalKind === undefined;
  // Alarme de parada: ritmo zero + fresco além da tolerância.
  const stop = resolveStopAlarm(widget);
  const alarming = useStopAlarm({
    enabled: stop.enabled,
    stopped: isFresh && rate === 0,
    fresh: isFresh,
    afterMinutes: stop.afterMinutes,
  });
  // O diagnóstico VENCE a frase de processo. Sem ele, uma falha elétrica AIN
  // (que anula o valor mas mantém `isAvailable`) caía em `rate === null` e o
  // card dizia "Parada agora" — fio partido apresentado como máquina parada.
  const diagnosed = describeSignalDiagnostic(value.diagnostic);
  const baseMeaning: { tone: HeroTone; text: string } | null = isLegacyVelocity
    ? resolveRateMeaning(isFresh, rate)
    : !isFresh ? { tone: "warn", text: "Sem leitura recente" } : null;
  const meaning: { tone: HeroTone; text: string } | null =
    diagnosed ?? (alarming ? { tone: "danger", text: "Parada além do tolerado" } : baseMeaning);
  const cardTone =
    meaning?.tone === "danger" ? "danger" : meaning?.tone === "warn" ? "warn" : "ok";

  return (
    <HeroCard
      title={widget.title}
      caption={widget.settings.signalKind === "rate" ? "Leitura atual" : "Velocidade atual"}
      tone={cardTone}
      alarm={alarming || widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      onHideChart={onHideChart}
      footnote={(() => {
        const rel = value.measuredAt ? formatWidgetRelative(value.measuredAt) : null;
        const text = isFresh ? `Atualizado ${rel ?? "agora"}` : rel ? `Última leitura ${rel}` : null;
        if (!data || !hasPlottable || stats.mean === null) return text;
        return (
          <>
            {text ? <span>{text}</span> : null}
            <TrendStats stats={stats} unit={unit} decimals={decimals} window={dataWindow} />
          </>
        );
      })()}
    >
      <div className="w-hero__value">
        {formatWidgetNumber(rate, decimals)}
        {unit ? <span className="w-hero__unit">{unit}</span> : null}
      </div>

      {meaning ? <HeroMeaning tone={meaning.tone}>{meaning.text}</HeroMeaning> : null}

      <div
        className={busy ? "w-hero__trend w-hero__trend--busy" : "w-hero__trend"}
        aria-busy={busy || undefined}
      >
        <div className="w-hero__trend-hd">
          <span>Ao longo {phrase}</span>
          {onChangeWindow ? (
            <HeroWindowPicker
              value={config.window}
              busy={busy}
              onChange={onChangeWindow}
              ariaLabel={`Período de ${widget.title}`}
            />
          ) : null}
        </div>
        {data && hasPlottable ? (
          <TimeSeriesChart
            points={points}
            kind="line"
            window={dataWindow}
            unit={unit ?? undefined}
            decimals={decimals}
            yScale={config.yScale}
            min={widget.settings.min}
            max={widget.settings.max}
            thresholds={widget.settings.thresholds}
            title={widget.title}
            reference={stats.reference}
            valueLabel="média"
          />
        ) : (
          <p className="w-hero__trend-empty">
            {status === "unavailable"
              ? "Ative o Timescale para ver a linha de ritmo."
              : status === "error"
                ? "Não foi possível carregar a linha."
                : status === "ready"
                  ? "Ainda não há amostras nesta janela."
                  : "Carregando linha…"}
          </p>
        )}
      </div>
    </HeroCard>
  );
}

function resolveRateMeaning(
  isFresh: boolean,
  rate: number | null,
): { tone: HeroTone; text: string } {
  if (!isFresh) return { tone: "warn", text: "Sem leitura recente" };
  if (rate !== null && rate !== 0) return { tone: "ok", text: "Rodando" };
  return { tone: "muted", text: "Parada agora" };
}
